/**
 * The chart palette (docs/design-system/language/data-viz.md): 8 categorical slots from the
 * tokens (`--data-1` to `--data-8`), assigned in order and never cycled. At most 6 series in
 * one chart; the rest fold into "Other" in `--fg-subtle`. Slots 7 and 8 are for identity, not
 * for series side by side. Never a status colour for a series.
 */

export const DATA_SLOTS = 8;
/** The most series one chart shows before folding the rest into "Other". */
export const MAX_SERIES = 6;
export const OTHER_COLOUR = 'var(--fg-subtle)';
export const OTHER_KEY = '__other';

/** The colour of slot `i` (0-based): `var(--data-1)` for the first series. */
export function slotColour(i: number): string {
  return i >= 0 && i < DATA_SLOTS ? `var(--data-${i + 1})` : OTHER_COLOUR;
}

/**
 * Stable colours by series key: a series keeps its slot across every chart on a page as long
 * as the same `order` (every key the page knows, in slot order) is passed. Keys past the
 * sixth, or unknown, are "Other".
 */
export function colourFor(key: string, order: readonly string[]): string {
  if (key === OTHER_KEY) return OTHER_COLOUR;
  const i = order.indexOf(key);
  return i >= 0 && i < MAX_SERIES ? slotColour(i) : OTHER_COLOUR;
}

/**
 * Keep the first `max - 1` items and fold the rest into one "Other" item when there are more
 * than `max`, so a chart never shows more than `max` series.
 */
export function foldOther<T extends { key: string; label: string }>(items: readonly T[], max: number, merge: (rest: T[]) => Omit<T, 'key' | 'label'>): T[] {
  if (items.length <= max) return [...items];
  const head = items.slice(0, max - 1);
  const rest = items.slice(max - 1);
  return [...head, { ...merge(rest), key: OTHER_KEY, label: `Other (${rest.length})` } as T];
}
